import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Beaker, Shield, Zap, Award } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { products, testimonials } from '../data/mockData';
import StockBadge from '../components/StockBadge';
import NewBadge from '../components/NewBadge';
import BundlesSection from '../components/BundlesSection';
import BulkBuyBanner from '../components/BulkBuyBanner';
import Seo from '../components/Seo';
import { Stars } from '../components/ReviewSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const featuredProducts = products.slice(0, 6);
  const [reviewStats, setReviewStats] = useState({});

  useEffect(() => {
    axios
      .get(`${API}/reviews/stats`)
      .then((res) => setReviewStats(res.data || {}))
      .catch(() => setReviewStats({}));
  }, []);

  const features = [
    {
      icon: Shield,
      title: 'Quality Assured',
      description: 'Strict quality control protocols for consistent research materials'
    },
    {
      icon: Beaker,
      title: 'Research Grade',
      description: 'Pharmaceutical-grade peptides manufactured in specialized facilities'
    },
    {
      icon: Zap,
      title: 'Fast Shipping',
      description: 'Quick and secure delivery with proper handling'
    },
    {
      icon: Award,
      title: 'Trusted Source',
      description: 'Serving research professionals with reliable products'
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0C0A09' }}>
      <Seo
        path="/"
        title="Eman's Peppers | Research Peptides"
        description="Eman's Peppers is a US-based research peptide supplier — Retatrutide, Tirzepatide, BPC-157, GHK-Cu, MOTS-C and more for laboratory research only. Not a food brand or recipe site."
      />
      {/* SEO H1 — semantic brand H1 for search engines. Visually hidden so design is unchanged. */}
      <h1 className="sr-only" data-testid="home-h1">Eman's Peppers</h1>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Warm Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/fac60aef-8756-4630-9396-93444e51dc26/images/bd43a1f39ab7f6b173f18647429a7259ad7f30101b47f020d05e0ce7eaaa4a8a.jpeg"
            alt="Research peptide vials"
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0C0A09] via-[#0C0A09]/60 to-[#0C0A09]" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="hero-title fade-in" role="heading" aria-level="2">
              Premium Research Peptides
              <span className="block mt-2 italic" style={{ color: '#D4A373' }}>
                for Optimal Wellness
              </span>
            </div>
            <p className="hero-subtitle fade-in" style={{ animationDelay: '0.2s' }}>
              Laboratory-tested, research-grade peptides engineered for peak performance, recovery, and longevity. Your trusted source for pharmaceutical excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center fade-in" style={{ animationDelay: '0.4s' }}>
              <Link to="/products">
                <button className="btn-primary">
                  Shop Products
                  <ArrowRight className="ml-2 h-5 w-5 inline" />
                </button>
              </Link>
              <Link to="/about">
                <button className="btn-secondary">
                  Learn More
                </button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="animate-bounce">
            <div className="w-6 h-10 border-2 rounded-full flex items-start justify-center p-2" style={{ borderColor: 'rgba(212, 163, 115, 0.5)' }}>
              <div className="w-1 h-3 rounded-full" style={{ backgroundColor: '#D4A373' }} />
            </div>
          </div>
        </div>
      </section>

      {/* Crazy Bundle Saves - hyped placement right after hero */}
      <BundlesSection />

      {/* Bulk Buy CTA */}
      <BulkBuyBanner />

      {/* Features Section */}
      <section className="py-24" style={{ backgroundColor: '#1A1513' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">Why Choose CM Peptides</h2>
            <p className="section-subtitle">
              Uncompromising quality standards meet cutting-edge peptide science
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="card p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-sm mb-6" style={{ backgroundColor: '#D4A373', color: '#0C0A09' }}>
                  <feature.icon className="h-8 w-8" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-medium mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#F7F3EE' }}>{feature.title}</h3>
                <p style={{ color: '#B5A79E' }}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24" style={{ backgroundColor: '#0C0A09' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">Featured Products</h2>
            <p className="section-subtitle">
              Explore our most popular research peptides
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {featuredProducts.map(product => (
              <div 
                key={product.id} 
                className="product-card cursor-pointer"
                onClick={() => window.location.href = `/product/${product.id}`}
              >
                <div className="product-image-wrapper relative">
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="product-image"
                    style={product.stockStatus === 'out_of_stock' ? { opacity: 0.45, filter: 'grayscale(0.6)' } : undefined}
                  />
                  {product.stockStatus && product.stockStatus !== 'in_stock' && (
                    <div className="absolute top-3 left-3 z-10">
                      <StockBadge status={product.stockStatus} count={product.stockCount} note={product.restockNote} size="md" />
                    </div>
                  )}
                  {product.isNew && (
                    <div className={`absolute ${product.stockStatus && product.stockStatus !== 'in_stock' ? 'top-12' : 'top-3'} left-3 z-10`}>
                      <NewBadge size="md" />
                    </div>
                  )}
                  {product.isBundle && (
                    <div className="absolute top-3 right-3 z-10">
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded"
                        style={{
                          background: 'linear-gradient(135deg, #D4A373 0%, #C78C50 100%)',
                          color: '#0C0A09',
                          boxShadow: '0 4px 12px rgba(212, 163, 115, 0.4)',
                        }}
                      >
                        🎁 Bundle
                      </span>
                    </div>
                  )}
                </div>
                <div className="product-info">
                  <span className="product-category">{product.category}</span>
                  <h3 className="product-name">{product.name}</h3>
                  {reviewStats[product.id] && reviewStats[product.id].count > 0 && (
                    <div className="flex items-center gap-1.5 mb-2" data-testid={`home-card-rating-${product.id}`}>
                      <Stars value={reviewStats[product.id].avg} size="sm" />
                      <span className="text-xs" style={{ color: '#B5A79E' }}>
                        {reviewStats[product.id].avg.toFixed(1)} ({reviewStats[product.id].count})
                      </span>
                    </div>
                  )}
                  <p className="product-description line-clamp-2">{product.description}</p>
                  <div className="flex items-center justify-between mt-auto">
                    <div className="flex items-baseline gap-2">
                      <span className="product-price">${product.price}</span>
                      {product.onSale && product.originalPrice && (
                        <span className="text-sm line-through" style={{ color: '#5A4A3F' }}>
                          ${product.originalPrice}
                        </span>
                      )}
                    </div>
                    <Link to={`/product/${product.id}`} onClick={(e) => e.stopPropagation()}>
                      <button className="btn-secondary text-xs px-4 py-2">
                        View Details
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/products">
              <button className="btn-primary px-12 py-4">
                View All Products
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24" style={{ backgroundColor: '#1A1513' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">What Our Clients Say</h2>
            <p className="section-subtitle">
              Trusted by researchers and wellness professionals worldwide
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map(testimonial => (
              <div key={testimonial.id} className="card p-8">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 fill-current" style={{ color: '#D4A373' }} viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="mb-6 italic" style={{ color: '#B5A79E', fontFamily: 'Manrope, sans-serif' }}>"{testimonial.content}"</p>
                <div>
                  <p className="font-bold" style={{ color: '#F7F3EE', fontFamily: 'Playfair Display, serif' }}>{testimonial.name}</p>
                  <p className="text-sm" style={{ color: '#B5A79E' }}>{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r" style={{ backgroundImage: 'linear-gradient(to right, #D4A373, #C78C50)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-medium mb-6" style={{ color: '#0C0A09', fontFamily: 'Playfair Display, serif' }}>
            Ready to Optimize Your Wellness?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto" style={{ color: '#0C0A09', opacity: 0.8, fontFamily: 'Manrope, sans-serif' }}>
            Join thousands of satisfied customers who trust CM Peptides for their research needs
          </p>
          <Link to="/products">
            <button className="px-8 py-4 text-lg font-medium rounded-sm transition-all hover:scale-105" style={{ backgroundColor: '#0C0A09', color: '#F7F3EE' }}>
              Start Shopping Now
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
